/** Users · /app home — shows the recruiter's preferred dashboard view.
 *
 *  Network View (/app/network) is the default on desktop; Classic (the original Dashboard) is one
 *  click away in the profile menu, and whichever the recruiter last picked there is remembered per
 *  browser. On phones /app always opens Classic.
 *  The Dashboard component itself is unchanged — this only decides which view /app opens. */
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Dashboard } from "./Dashboard.js";

export type DashboardView = "network" | "classic";

const KEY = "th:dashboard-view";

export function getDashboardView(): DashboardView {
  try {
    return localStorage.getItem(KEY) === "classic" ? "classic" : "network";
  } catch {
    return "network"; // storage blocked: fall back to the default view
  }
}

export function setDashboardView(view: DashboardView): void {
  try {
    localStorage.setItem(KEY, view);
  } catch {
    /* preference is best-effort */
  }
}

/** The Network View is a pannable desktop canvas; on a phone- or small-tablet-width screen /app
 *  always opens Classic, whatever was saved (the saved choice still applies on a wider screen). */
const NARROW = "(max-width: 900px)";

function isNarrow(): boolean {
  try {
    return window.matchMedia(NARROW).matches;
  } catch {
    return false;
  }
}

export function DashboardHome(): ReactNode {
  return getDashboardView() === "network" && !isNarrow() ? <Navigate to="/app/network" replace /> : <Dashboard />;
}
